import { useApolloClient } from '@apollo/client'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { useAgreeAdGuidelineMutation, ViewerDocument, ViewerQuery } from '~/generated/graphql'
import { DateTime } from 'luxon'
import { useCallback, useState } from 'react'

type Props = {
  open: boolean
  url: string
  latestVersion: DateTime
  onClose?: VoidFunction
}

export const AdGuidelineDialog = ({ open, url, latestVersion, onClose }: Props) => {
  const [checked, setChecked] = useState(false)
  const handleChange = useCallback((_e: unknown, checked: boolean) => setChecked(checked), [])
  const apolloClient = useApolloClient()

  const [agreeAdGuideline, { loading: aLoading, error: aError }] = useAgreeAdGuidelineMutation()
  const handleAgree = useCallback(async () => {
    if (!checked) return
    // NOTE: AgreeAdGuidelineMutationの戻り値はViewerではないのでID一致によるViewerキャッシュの自動更新が行われない
    // そのため、手動でキャッシュを更新する
    const { data } = await agreeAdGuideline()
    apolloClient.cache.updateQuery<ViewerQuery>(
      {
        query: ViewerDocument,
      },
      (prev) => {
        if (!data?.agreeAdGuideline) return prev
        return {
          ...prev,
          viewer: {
            ...prev?.viewer,
            adGuideline: {
              ...prev?.viewer.adGuideline,
              ...data.agreeAdGuideline,
            },
          },
        }
      },
    )
    onClose?.()
  }, [apolloClient, agreeAdGuideline, checked, onClose])

  return (
    <Dialog open={open}>
      {/* ↑任意のタイミングで閉じられたくないのでonCloseは渡さない */}
      <DialogTitle
        sx={{
          color: 'primary.dark',
          fontWeight: 'bold',
        }}
      >
        最新の広告ガイドラインに同意してください
      </DialogTitle>
      <DialogContent>
        <Stack gap={0.5}>
          <FormControlLabel
            control={<Checkbox />}
            onChange={handleChange}
            label={
              <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                <Link href={url} target="_blank">
                  toridori marketing 広告ガイドライン
                  <OpenInNew fontSize="inherit" />
                </Link>
                に同意します。
              </DialogContentText>
            }
          />
          <Typography variant="caption" ml={4} color="text.secondary">
            最新のバージョン：{latestVersion.toLocaleString()}
          </Typography>
          {aError && <Alert severity="error">{aError.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={aLoading} disabled={!checked} onClick={handleAgree}>
          同意して続ける
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
